import { useEffect, useState, useMemo } from "react";
import { Box, Typography, Chip, Input, Button } from "@mui/joy";
import { Paper } from "@mui/material";
import {
  Check,
  CheckCircle,
  FileDownload,
  PictureAsPdf,
} from "@mui/icons-material";
import { connect } from "react-redux";
import DataTable from "react-data-table-component";
import {
  GET_ACTIVE_DRIVERS_API_URL,
  GET_TRANSACTION_HISTORY_API_URL,
} from "../../configs/url";
import apiCall, { flatten } from "../../helpers/api";
import {
  datatable_style,
  downloadTablePdf,
  exportToCsv,
  LoadingComponent,
  NoDataComponent,
} from "../../helpers/datatable_helpers";
import { data } from "jquery";
import { useTranslation } from "react-i18next";

const Drivers = (props) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [bonusData, setBonusData] = useState([]);
  const { t } = useTranslation();

  const [forceUpdate, setForceUpdate] = useState(0);

  const settings = props?.settings || [];

  useEffect(() => {
    if (!props?.user || !props?.user?.auth_token) {
      window.location.href = "/login";
    }
    document.title = t("sidebar_drivers") + " | KPG Partner";
  }, []);

  const dataColumns = [
    {
      data: "name",
      name: "name",
      searchable: true,
      title: t("drivers_column_name"),
    },
    {
      data: "phone",
      name: "phone",
      searchable: true,
      title: t("drivers_column_phone"),
    },
    {
      data: "created_at",
      name: "created_at",
      searchable: true,
      title: t("drivers_column_created_at"),
    },
    {
      data: "orders",
      name: "orders",
      searchable: true,
      title: t("drivers_column_orders"),
    },
    {
      data: "days_left",
      name: "days_left",
      searchable: true,
      title: t("drivers_column_days_left"),
    },
    {
      data: "bonus_achievement",
      name: "bonus_achievement",
      searchable: true,
      title: t("drivers_column_bonus_achievement"),
    },
    {
      data: "next_bonus",
      name: "next_bonus",
      searchable: true,
      title: t("drivers_column_next_bonus"),
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    const data = {
      draw: 1,
      start: perPage * (page - 1),
      length: perPage,
      search: { value: search, regex: false },
      columns: dataColumns,
    };
    try {
      const res = await apiCall({
        method: "POST",
        url:
          GET_ACTIVE_DRIVERS_API_URL + "?" + new URLSearchParams(flatten(data)),
      });
      const processedData = res.data.data.original.data.map((row) => ({
        name: row.name || "",
        phone: row.phone || "",
        created_at: row.created_at || "NA",
        orders: row.orders || 0,
        days_left: row.days_left || 0,
        bonus_achievement: row.bonus_achievement || 0,
        next_bonus: row.next_bonus || 0,
      }));
      setBonusData(res.data.refer_earn_steps);
      setRows(processedData);
      setFilteredRows(processedData);
      setTotal(res.data.recordsFiltered);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearch(searchValue);
    setPage(1);
  };

  useEffect(() => {
    fetchData();
  }, [props?.user, search, page, perPage]);

  const columns = useMemo(
    () => [
      {
        name: t("drivers_column_name"),
        selector: (row) => row.name,
        sortable: true,
        minWidth: "120px",
        maxWidth: "260px",
        cell: (row) => (
          <Typography
            level="body-sm"
            className="description-text"
            title={row.name}
          >
            {row.name}
          </Typography>
        ),
      },
      {
        name: t("drivers_column_phone"),
        selector: (row) => row.phone,
        sortable: true,
        minWidth: "120px",
        // maxWidth: "150px",
        cell: (row) => (
          <Typography
            level="body-sm"
            className="description-text"
            title={row.phone}
          >
            {row.phone}
          </Typography>
        ),
      },
      {
        name: t("drivers_column_created_at"),
        selector: (row) => row.created_at,
        sortable: true,
        minWidth: "160px",
        maxWidth: "200px",
        cell: (row) => (
          <Typography level="body-sm">{row.created_at}</Typography>
        ),
      },
      {
        name: t("drivers_column_orders"),
        selector: (row) => row.orders,
        sortable: true,
        minWidth: "120px",
        maxWidth: "160px",
        cell: (row) => (
          <Typography component="span" className="amount-text">
            {row.orders}
          </Typography>
        ),
      },
      {
        name: t("drivers_column_bonus_achievement"),
        selector: (row) => row.bonus_achievement,
        sortable: true,
        minWidth: "275px",
        cell: (row) => {
          // Find the current step based on orders
          let achievedPoints = Number(row.bonus_achievement) || 0;
          let totalPoints = bonusData
            .map((step) => Number(step.bonus_point))
            .reduce((acc, val) => acc + val, 0);
          let progress = totalPoints ? (achievedPoints / totalPoints) * 100 : 0;

          // Find the current step for tooltip or label
          let currentStep =
            Number(row.orders) === 0
              ? bonusData[0]
              : bonusData.find(
                  (step) => Number(row.orders) < Number(step.order_count)
                ) || bonusData[bonusData.length - 1];

          return (
            <Box sx={{ minWidth: 250 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                <Typography level="body-sm" sx={{ mr: 1 }}>
                  {settings[5].value} {achievedPoints} / {settings[5].value}{" "}
                  {totalPoints}
                </Typography>
                {progress >= 100 && (
                  <Chip
                    variant="soft"
                    color="success"
                    size="sm"
                    className="status-chip p-1"
                    label="Achieved"
                  >
                    <CheckCircle fontSize="small" className="text-success" />
                  </Chip>
                )}
              </Box>
              <Box sx={{ width: "100%" }}>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    background: "#e0e0e0",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${progress}%`,
                      height: "100%",
                      background:
                        progress >= 100
                          ? "#4caf50"
                          : "linear-gradient(90deg, #ffcc00 60%, #000000 100%)",
                      transition: "width 0.3s",
                    }}
                  />
                </Box>
              </Box>
              <Typography level="body-xs" sx={{ mt: 0.5, color: "#888" }}>
                {t("common_next")}:{" "}
                {currentStep
                  ? `${currentStep.order_count} ${t("common_orders")} = ${
                      settings[5].value
                    } ${currentStep.bonus_point}`
                  : "Max achieved"}
              </Typography>
            </Box>
          );
        },
      },
      {
        name: t("drivers_column_days_left"),
        selector: (row) => row.days_left,
        sortable: true,
        minWidth: "120px",
        // maxWidth: "160px",
        cell: (row) => (
          <Typography component="span" className="amount-text">
            {row.days_left}
          </Typography>
        ),
      },
      {
        name: t("drivers_column_next_bonus"),
        selector: (row) => row.next_bonus,
        sortable: true,
        minWidth: "120px",
        // maxWidth: "160px",
        cell: (row) =>
          row.next_bonus === "achieved" ? (
            <Chip
              variant="soft"
              color="success"
              size="sm"
              className="status-chip p-2"
              label={t("drivers_achieved")}
            >
              <CheckCircle className="text-success" /> 100%{" "}
              {t("drivers_achieved")}
            </Chip>
          ) : (
            <Typography level="body-sm">{row.next_bonus}</Typography>
          ),
      },
    ],
    [bonusData, settings]
  );

  return (
    <Box className="transactions-container">
      <Box className="header-container">
        <Typography level="h4">{t("sidebar_drivers")}</Typography>
        <Box className="controls-container">
          <Input
            size="sm"
            placeholder={t("drivers_search")}
            value={search}
            onChange={handleSearchChange}
            className="search-input"
          />
          <Box className="export-buttons-container">
            <Button
              size="sm"
              variant="soft"
              onClick={() => exportToCsv(filteredRows, "drivers")}
              disabled={total === 0}
              startDecorator={<FileDownload sx={{ fontSize: "16px" }} />}
              className="export-btn csv-btn"
            >
              {t("common_csv")}
            </Button>
            <Button
              size="sm"
              variant="soft"
              onClick={() => downloadTablePdf(columns, filteredRows, "drivers")}
              disabled={total === 0}
              startDecorator={<PictureAsPdf sx={{ fontSize: "16px" }} />}
              className="export-btn pdf-btn"
            >
              {t("common_pdf")}
            </Button>
          </Box>
        </Box>
      </Box>

      <Box className="table-wrapper">
        <Paper elevation={0} className="table-paper">
          <DataTable
            columns={columns}
            data={filteredRows}
            paginationServer
            paginationTotalRows={total}
            progressPending={loading}
            progressComponent={<LoadingComponent />}
            noDataComponent={<NoDataComponent />}
            pagination
            paginationPerPage={perPage}
            onChangeRowsPerPage={(newPerPage) => {
              setPerPage(newPerPage);
              setPage(1);
            }}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            responsive
            striped
            highlightOnHover
            customStyles={datatable_style}
            paginationComponentOptions={{
              rowsPerPageText: t("common_rows_per_page"),
              rangeSeparatorText: t("common_dt_separator"),
              selectAllRowsItem: true,
              selectAllRowsItemText: t("common_all"),
            }}
            onChangePage={(page) => {
              setPage(page);
            }}
            dense={window.innerWidth < 768}
          />
        </Paper>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  settings: state.settings.settings,
});
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(Drivers);
